import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FileEdit, Calendar, Mail, UserCheck,
  Users, LogOut, GraduationCap, UsersRound,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { OverviewPanel } from "./admin/OverviewPanel";
import { ContentPanel } from "./admin/ContentPanel";
import { EventsPanel } from "./admin/EventsPanel";
import { UsersPanel } from "./admin/UsersPanel";
import { MembershipPanel } from "./admin/MembershipPanel";
import { ContactPanel } from "./admin/ContactPanel";
import { NewsletterPanel } from "./admin/NewsletterPanel";
import { SymposiumAdminPanel } from "./admin/SymposiumPanel";
import { StudentChaptersPanel } from "./admin/StudentChaptersPanel";


const SECTIONS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "content", label: "Site Content", icon: FileEdit },
  { id: "events", label: "Events & Reservations", icon: Calendar },
  { id: "symposium", label: "Symposium", icon: GraduationCap },
  { id: "student_chapters", label: "Student Chapters", icon: GraduationCap },
  { id: "users", label: "Users & Roles", icon: UsersRound },
  { id: "membership", label: "Membership Applications", icon: UserCheck },
  { id: "contact", label: "Contact Submissions", icon: Mail },
  { id: "newsletter", label: "Newsletter", icon: Users },
];


export default function AdminPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [section, setSection] = useState("overview");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [section]);

  const handleLogout = async () => {
    nav("/", { replace: true });
    await logout();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--ser-bg)" }} data-testid="admin-page">
      <div className="flex flex-col md:flex-row">
        <aside
          className="md:w-72 border-r md:min-h-screen p-6"
          style={{ backgroundColor: "var(--ser-surface)", borderColor: "var(--ser-sand-dark)" }}
        >
          <div className="mb-8">
            <BrandLogo height={52} linkTo="/" />
            <p className="text-xs mt-2" style={{ color: "var(--ser-text-muted)" }}>Admin Panel</p>
          </div>
          <nav className="space-y-1">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                className={`admin-nav-item ${section === s.id ? "active" : ""}`}
                data-testid={`admin-nav-${s.id}`}
              >
                <s.icon className="w-4 h-4" />
                {s.label}
              </button>
            ))}
          </nav>
          <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--ser-sand-dark)" }}>
            <p className="text-xs mb-2" style={{ color: "var(--ser-text-muted)" }}>{user?.email}</p>
            <button onClick={handleLogout} className="admin-nav-item" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </aside>

        <main className="flex-1 p-6 md:p-10">
          {section === "overview" && <OverviewPanel stats={stats} onGo={setSection} />}
          {section === "content" && <ContentPanel />}
          {section === "events" && <EventsPanel />}
          {section === "symposium" && <SymposiumAdminPanel />}
          {section === "student_chapters" && <StudentChaptersPanel />}
          {section === "users" && <UsersPanel currentUser={user} />}
          {section === "membership" && <MembershipPanel />}
          {section === "contact" && <ContactPanel />}
          {section === "newsletter" && <NewsletterPanel />}
        </main>
      </div>
    </div>
  );
}
